import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr, STATUS_STYLES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ScheduleAuditDialog } from "../components/ScheduleAuditDialog";
import { ImportPastAuditDialog } from "../components/ImportPastAuditDialog";
import { Plus, Play, FileText, Trash2, Repeat, Pause, CirclePlay, History, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Audits() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSchedule = ["corporate_admin", "regional_head", "hotel_gm"].includes(user.role);
  const [audits, setAudits] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => {
    api.get("/audits").then((r) => setAudits(r.data));
    if (canSchedule) api.get("/schedules").then((r) => setSchedules(r.data));
  };
  useEffect(() => {
    load();
  }, []);

  const toggleSchedule = async (id) => {
    const { data } = await api.put(`/schedules/${id}/toggle`);
    toast.success(data.active ? "Schedule resumed" : "Schedule paused");
    load();
  };

  const removeSchedule = async (id) => {
    await api.delete(`/schedules/${id}`);
    toast.success("Schedule deleted");
    load();
  };

  const removeAudit = async (id) => {
    await api.delete(`/audits/${id}`);
    toast.success("Audit deleted");
    load();
  };

  const canRun = (a) => a.status !== "completed" && (a.auditor_id === user.id || (a.assigned_auditor_ids || []).includes(user.id) || user.role === "corporate_admin");
  const canAuthorize = (a) => !a.authorized && a.status === "scheduled" && ["corporate_admin", "regional_head", "hotel_gm"].includes(user.role);

  const authorize = async (id) => {
    try {
      await api.put(`/audits/${id}/authorize`);
      toast.success("Audit authorized — auditors notified");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="audits-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Audits</h1>
          <p className="text-stone-500 mt-1 text-sm">Schedule, execute and review audits.</p>
        </div>
        {canSchedule && (
          <div className="flex flex-wrap gap-3">
            {user.role === "corporate_admin" && (
              <>
                <Button data-testid="import-past-audit-button" variant="outline" onClick={() => setImportOpen(true)}>
                  <History className="w-4 h-4 mr-2" />Import Past Audit
                </Button>
                <ImportPastAuditDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
              </>
            )}
            <Button data-testid="schedule-audit-button" className="bg-[#14532D] hover:bg-[#166534]" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />Schedule Audit
            </Button>
            <ScheduleAuditDialog open={open} onOpenChange={setOpen} onCreated={load} />
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead className="bg-stone-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
              <th className="py-3 px-4">Audit</th>
              <th className="py-3 px-4">Hotel</th>
              <th className="py-3 px-4">Type</th>
              <th className="py-3 px-4">Auditor</th>
              <th className="py-3 px-4">Due</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Score</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="audits-table">
            {audits.map((a) => (
              <tr key={a.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60">
                <td className="py-3.5 px-4 text-sm font-medium text-stone-800">{a.template_name}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600 max-w-[200px] truncate">{a.hotel_name}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600 capitalize">{a.audit_type}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{a.auditor_name}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600 font-mono">{a.due_date?.slice(0, 10)}</td>
                <td className="py-3.5 px-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLES[a.status]}`}>{a.status.replace("_", " ")}</span>
                </td>
                <td className="py-3.5 px-4 text-right font-mono text-sm font-semibold">{a.scores?.overall_score != null ? `${a.scores.overall_score}%` : "—"}</td>
                <td className="py-3.5 px-4 text-right whitespace-nowrap">
                  {canAuthorize(a) && (
                    <Button data-testid={`audit-authorize-${a.id}`} size="sm" className="mr-2 bg-amber-600 hover:bg-amber-700" onClick={() => authorize(a.id)}>
                      <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Authorize
                    </Button>
                  )}
                  {canRun(a) && (
                    <Button data-testid={`audit-run-${a.id}`} size="sm" variant="outline" className="mr-2" disabled={a.authorized === false} onClick={() => navigate(`/audits/${a.id}/run`)}>
                      <Play className="w-3.5 h-3.5 mr-1.5" />{a.status === "in_progress" ? "Continue" : "Start"}
                    </Button>
                  )}
                  {a.status === "completed" && (
                    <Button data-testid={`audit-report-${a.id}`} size="sm" variant="outline" className="mr-2" onClick={() => navigate(`/audits/${a.id}/report`)}>
                      <FileText className="w-3.5 h-3.5 mr-1.5" />Report
                    </Button>
                  )}
                  {user.role === "corporate_admin" && (
                    <button className="p-1.5 rounded hover:bg-rose-50 text-rose-500" onClick={() => removeAudit(a.id)} data-testid={`audit-delete-${a.id}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {audits.length === 0 && <tr><td colSpan={8} className="py-12 text-center text-sm text-stone-400">No audits yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {canSchedule && schedules.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Repeat className="w-4 h-4 text-[#14532D]" strokeWidth={1.5} />
            <h2 className="text-lg font-bold tracking-tight text-stone-900">Recurring Schedules</h2>
          </div>
          <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-x-auto">
            <table className="w-full min-w-[780px]">
              <thead className="bg-stone-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
                  <th className="py-3 px-4">Audit</th>
                  <th className="py-3 px-4">Hotel</th>
                  <th className="py-3 px-4">Auditor</th>
                  <th className="py-3 px-4">Frequency</th>
                  <th className="py-3 px-4">Next Run</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="schedules-table">
                {schedules.map((s) => (
                  <tr key={s.id} className={`border-b border-stone-100 last:border-0 hover:bg-stone-50/60 ${!s.active ? "opacity-50" : ""}`}>
                    <td className="py-3.5 px-4 text-sm font-medium text-stone-800">{s.template_name}</td>
                    <td className="py-3.5 px-4 text-sm text-stone-600 max-w-[200px] truncate">{s.hotel_name}</td>
                    <td className="py-3.5 px-4 text-sm text-stone-600">{s.auditor_name}</td>
                    <td className="py-3.5 px-4 text-sm text-stone-600 capitalize">{s.frequency}</td>
                    <td className="py-3.5 px-4 text-sm font-mono text-stone-600">{s.next_run?.slice(0, 10)}</td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${s.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-100 text-stone-500 border-stone-200"}`}>{s.active ? "Active" : "Paused"}</span>
                    </td>
                    <td className="py-3.5 px-4 text-right whitespace-nowrap">
                      <button data-testid={`schedule-toggle-${s.id}`} className="p-1.5 rounded hover:bg-stone-100 text-stone-500" onClick={() => toggleSchedule(s.id)} title={s.active ? "Pause" : "Resume"}>
                        {s.active ? <Pause className="w-4 h-4" strokeWidth={1.5} /> : <CirclePlay className="w-4 h-4" strokeWidth={1.5} />}
                      </button>
                      <button data-testid={`schedule-delete-${s.id}`} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" onClick={() => removeSchedule(s.id)}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
